/**
 * /handoff — public route. One click from the Bynaus app's firm menu ("Open Ledger Core"):
 * the Bynaus api got a one-time code for the person signed in to Bynaus and opened
 * /handoff#code=… (a fragment, so the code never reaches a server or an access log); this
 * page takes it out of the address bar, spends it for a Ledger Core session and goes to the
 * dashboard. A used or expired code shows the way back to the Bynaus app this stack is
 * configured with (no dead end).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/store";
import { api } from "@/lib/api";
import type { User } from "@/lib/types";

export const Route = createFileRoute("/handoff")({ component: Handoff });

function Handoff() {
  const setAuth = useAuth((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard: the code is single use
    ran.current = true;
    const code = new URLSearchParams(window.location.hash.slice(1)).get("code") ?? "";
    window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      try {
        // Direct fetch (not the api client) so no stale Ledger Core bearer is sent. An empty
        // code is sent too: the refusal names the Bynaus app to go back to.
        const resp = await fetch("/api/auth/bynaus-handoff/redeem", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          const detail = body.detail ?? {};
          if (typeof detail.back_url === "string") setBackUrl(detail.back_url);
          throw new Error(
            typeof detail.message === "string" ? detail.message : `Sign-in failed (${resp.status})`,
          );
        }
        const token = (await resp.json()) as { access_token: string };
        localStorage.setItem("ledger.auth.token", token.access_token);
        const me = await api<User>("/api/auth/me");
        setAuth(token.access_token, me);
        window.location.replace("/");
      } catch (exc: unknown) {
        setError(exc instanceof Error ? exc.message : "Sign-in failed");
      }
    })();
  }, [setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900 text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-red-600 mb-2">Could not open Ledger Core</h1>
            <p className="text-sm text-slate-500 mb-4 dark:text-slate-400">{error}</p>
            <p className="text-sm text-slate-500 mb-4 dark:text-slate-400">
              Open it again from the firm menu in Bynaus.
            </p>
            {backUrl ? (
              <a href={backUrl} className="text-sm text-blue-600 hover:text-blue-700">
                Go back to Bynaus
              </a>
            ) : (
              // Unreachable stack (no answer to read the address from): the Bynaus tab that
              // opened this one is still open behind it.
              <button
                type="button"
                onClick={() => window.close()}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Close this tab and go back to Bynaus
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">Opening Ledger Core…</p>
        )}
      </div>
    </div>
  );
}
